import { useState } from "react";
import { IonButton, IonIcon, IonInput, IonSelect, IonSelectOption, IonItem, IonLabel, IonToggle, IonNote } from "@ionic/react";
import { chevronDownOutline, chevronForwardOutline, trashOutline } from "ionicons/icons";
import { FLOAT } from "@/components/SettingsRows";
import * as US from "@/pages/app/AppPaystub";
import * as CA from "@/pages/app/AppCanadianPaystub";
import * as UK from "@/pages/app/AppUkPayslip";

const TYPES = { us: US, ca: CA, uk: UK };
const CURRENCY = { us: "$", ca: "CAD $", uk: "£" };
const LABELS = { us: "Contributions (401k, HSA, etc.)", ca: "Contributions (RRSP, TFSA, etc.)", uk: "Contributions (Pension, etc.)" };

// The employee modal's Additional options: the pay stub modal's optional
// sections kept on the employee, so picking them on a stub brings these
// along. Tax settings left blank keep the stub's own defaults; a list
// replaces the stub's when the employee has any entries in it. The sections
// wear the pay stub modal's look: a sub-bar per section, a period card per
// entry, the Add row flush at the foot. The types, currency and match benefit
// follow the employee's country; tax settings are the US stub's only.
const FILING = [["", "Default (rate below)"], ["single", "Single"], ["married", "Married Filing Jointly"], ["married_separately", "Married Filing Separately"], ["head_of_household", "Head of Household"]];
const TAX_FIELDS = [
  ["stateAllowances", "State Allowances"], ["federalTaxRate", "Federal Tax (%)"], ["stateTaxRate", "State Tax (%)"],
  ["socialSecurityRate", "Social Security (%)"], ["medicareRate", "Medicare (%)"], ["futaRate", "FUTA (%)"], ["sutaRate", "State Unemployment (%)"],
];
const newId = () => Date.now() + Math.floor(Math.random() * 1000);
const labelOf = (types, value) => (types.find((t) => t.value === value) || {}).label;
const Optional = () => <IonNote color="medium" className="optional-note">(Optional)</IonNote>;

export default function EmployeeExtras({ form, patch, country = "us" }) {
  const { deductionTypes, contributionTypes, employerBenefitTypes } = TYPES[country] || US;
  const cur = CURRENCY[country] || "$";
  // the employer match benefit: 401(k) in the US, RRSP in Canada, pension in the UK
  const MATCH = (employerBenefitTypes.find((t) => /_match$/.test(t.value)) || {}).value || "401k_match";
  const matchLabel = (employerBenefitTypes.find((t) => t.value === MATCH) || {}).label || "Employer Match";
  const [open, setOpen] = useState(false);
  const tax = form.taxSettings || {};
  const setTax = (k, v) => patch({ taxSettings: { ...tax, [k]: v } });
  const list = (k) => form[k] || [];
  const setList = (k, fn) => patch({ [k]: fn(list(k)) });
  const add = (k, item) => setList(k, (l) => [...l, { id: newId(), ...item }]);
  const remove = (k, id) => setList(k, (l) => l.filter((x) => x.id !== id));
  const update = (k, id, field, value, types) => setList(k, (l) => l.map((x) => {
    if (x.id !== id) return x;
    const next = { ...x, [field]: value };
    if (field === "type" && types) { const t = types.find((y) => y.value === value); if (t && value !== "other") next.name = t.label; }
    return next;
  }));

  const head = (title, i, suffix, onRemove, aria) => (
    <div className="admin-subbar sheet-card-head period-head with-action">
      <span>{title} {i + 1}{suffix ? `: ${suffix}` : ""}</span>
      <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={onRemove} aria-label={aria}>
        <IonIcon slot="icon-only" icon={trashOutline} />
      </IonButton>
    </div>
  );
  // a deduction's or contribution's fields
  const money = (k, d, types, preTaxLabel) => (
    <div className="period-form">
      <IonSelect {...FLOAT} interface="popover" label="Type" value={d.type} onIonChange={(e) => update(k, d.id, "type", e.detail.value, types)}>
        {types.map((t) => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
      </IonSelect>
      <IonInput {...FLOAT} label="Name / Description" value={d.name} onIonInput={(e) => update(k, d.id, "name", e.detail.value)} />
      <IonInput {...FLOAT} label={d.isPercentage ? "Amount (%)" : `Amount (${cur})`} type="number" inputmode="decimal" value={d.amount} onIonInput={(e) => update(k, d.id, "amount", e.detail.value)} />
      <IonSelect {...FLOAT} interface="popover" label="Unit" value={d.isPercentage ? "percent" : "amount"} onIonChange={(e) => update(k, d.id, "isPercentage", e.detail.value === "percent")}>
        <IonSelectOption value="amount">{country === "uk" ? `Pounds (${cur})` : `Dollars (${cur})`}</IonSelectOption>
        <IonSelectOption value="percent">Percent (%)</IonSelectOption>
      </IonSelect>
      <IonItem lines="none" className="admin-field period-wide period-toggle">
        <IonLabel style={{ fontSize: "0.85rem" }}>{preTaxLabel}</IonLabel>
        <IonToggle slot="end" checked={!!d.preTax} onIonChange={(e) => update(k, d.id, "preTax", e.detail.checked)} />
      </IonItem>
    </div>
  );
  const addRow = (label, onClick) => (
    <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={onClick}>
      <IonLabel color="primary">{label}</IonLabel>
    </IonItem>
  );

  return (
    <>
      <div className="extras-toggle">
        <IonButton fill="clear" size="small" color="dark" className="section-expander" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <IonIcon slot="start" icon={open ? chevronDownOutline : chevronForwardOutline} />
          Additional options
        </IonButton>
      </div>
      {open && (
        <>
          {country === "us" && (<>
          <div className="admin-subbar sheet-card-head">Tax Settings <Optional /></div>
          <div className="period-form">
            <IonSelect {...FLOAT} className="admin-field period-wide" interface="popover" label="Federal Filing Status" value={tax.federalFilingStatus ?? ""} onIonChange={(e) => setTax("federalFilingStatus", e.detail.value ?? "")}>
              {FILING.map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
            </IonSelect>
            {TAX_FIELDS.map(([k, label]) => (
              <IonInput key={k} {...FLOAT} label={label} type="number" inputmode="decimal" placeholder="Stub default" value={tax[k] ?? ""} onIonInput={(e) => setTax(k, e.detail.value ?? "")} />
            ))}
            <IonItem lines="none" className="admin-field period-wide period-toggle">
              <IonLabel style={{ fontSize: "0.85rem" }}>Include Local Tax</IonLabel>
              <IonToggle slot="end" checked={tax.includeLocalTax !== false} onIonChange={(e) => setTax("includeLocalTax", e.detail.checked)} />
            </IonItem>
            {tax.includeLocalTax !== false && (
              <IonInput {...FLOAT} className="admin-field period-wide" label="Local tax rate (%), blank = auto by city" type="number" inputmode="decimal" value={tax.customLocalTaxRate ?? ""} onIonInput={(e) => setTax("customLocalTaxRate", e.detail.value ?? "")} />
            )}
          </div>
          </>)}

          <div className="admin-subbar sheet-card-head">Deductions <Optional /></div>
          <div className="extras-list">
            {list("deductions").map((d, i) => (
              <div key={d.id} className="period-card">
                {head("Deduction", i, labelOf(deductionTypes, d.type), () => remove("deductions", d.id), "Remove deduction")}
                {money("deductions", d, deductionTypes, "Pre-Tax Deduction")}
              </div>
            ))}
            {addRow("Add Deduction", () => add("deductions", { type: "other", name: "", amount: "", isPercentage: false, preTax: false }))}
          </div>

          <div className="admin-subbar sheet-card-head">{LABELS[country] || LABELS.us} <Optional /></div>
          <div className="extras-list">
            {list("contributions").map((c, i) => (
              <div key={c.id} className="period-card">
                {head("Contribution", i, labelOf(contributionTypes, c.type), () => remove("contributions", c.id), "Remove contribution")}
                {money("contributions", c, contributionTypes, "Pre-Tax Contribution")}
              </div>
            ))}
            {addRow("Add Contribution", () => add("contributions", { type: "other", name: "", amount: "", isPercentage: false, preTax: false }))}
          </div>

          <div className="admin-subbar sheet-card-head">Employer Benefits <Optional /></div>
          <div className="extras-list">
            {list("employerBenefits").map((b, i) => (
              <div key={b.id} className="period-card">
                {head("Benefit", i, labelOf(employerBenefitTypes, b.type), () => remove("employerBenefits", b.id), "Remove benefit")}
                <div className="period-form">
                  <IonSelect {...FLOAT} className={`admin-field${b.type === MATCH ? " period-wide" : ""}`} interface="popover" label="Benefit Type" value={b.type} onIonChange={(e) => update("employerBenefits", b.id, "type", e.detail.value, employerBenefitTypes)}>
                    {employerBenefitTypes.map((t) => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
                  </IonSelect>
                  {b.type === MATCH ? (
                    <>
                      <IonInput {...FLOAT} label="Match %" type="number" inputmode="decimal" value={b.matchPercent || 50} onIonInput={(e) => update("employerBenefits", b.id, "matchPercent", e.detail.value)} />
                      <IonInput {...FLOAT} label="Up to % of Pay" type="number" inputmode="decimal" value={b.matchUpTo || 6} onIonInput={(e) => update("employerBenefits", b.id, "matchUpTo", e.detail.value)} />
                    </>
                  ) : (
                    <>
                      <IonInput {...FLOAT} label="Description" value={b.name} onIonInput={(e) => update("employerBenefits", b.id, "name", e.detail.value)} />
                      <IonInput {...FLOAT} label={b.isPercentage ? "Amount (%)" : `Amount (${cur})`} type="number" inputmode="decimal" value={b.amount} onIonInput={(e) => update("employerBenefits", b.id, "amount", e.detail.value)} />
                      <IonSelect {...FLOAT} interface="popover" label="Unit" value={b.isPercentage ? "percent" : "amount"} onIonChange={(e) => update("employerBenefits", b.id, "isPercentage", e.detail.value === "percent")}>
                        <IonSelectOption value="amount">{country === "uk" ? `Pounds (${cur})` : `Dollars (${cur})`}</IonSelectOption>
                        <IonSelectOption value="percent">Percent (%)</IonSelectOption>
                      </IonSelect>
                    </>
                  )}
                </div>
              </div>
            ))}
            {addRow("Add Employer Benefit", () => add("employerBenefits", { type: MATCH, name: matchLabel, amount: "", isPercentage: false, matchPercent: 50, matchUpTo: 6 }))}
          </div>

          <div className="admin-subbar sheet-card-head">Absence Plans (PTO, Vacation, Sick) <Optional /></div>
          <div className="extras-list">
            {list("absencePlans").map((p, i) => (
              <div key={p.id} className="period-card">
                {head("Absence Plan", i, p.description, () => remove("absencePlans", p.id), "Remove absence plan")}
                <div className="period-form">
                  <IonInput {...FLOAT} className="admin-field period-wide" label="Plan Name" value={p.description} onIonInput={(e) => update("absencePlans", p.id, "description", e.detail.value)} placeholder="e.g., PTO Plan" />
                  <IonInput {...FLOAT} label="Accrued (hrs)" type="number" inputmode="decimal" value={p.accrued} onIonInput={(e) => update("absencePlans", p.id, "accrued", e.detail.value)} />
                  <IonInput {...FLOAT} label="Reduced (hrs)" type="number" inputmode="decimal" value={p.reduced} onIonInput={(e) => update("absencePlans", p.id, "reduced", e.detail.value)} />
                </div>
              </div>
            ))}
            {addRow("Add Absence Plan", () => add("absencePlans", { description: "PTO Plan", accrued: "", reduced: "" }))}
          </div>
        </>
      )}
    </>
  );
}
